import { auth } from '../firebaseConfig';
import { EMAIL_NOT_VERIFIED_CODE } from '../authVerification';
import { ROUTES } from '../routes';
import { getGuestSessionId } from '../guestJourneySession';

const configuredBaseUrl = process.env.REACT_APP_JOURNEYBOT_API_URL;

export class JourneyBotApiError extends Error {
    constructor(message, status, details) {
        super(message);
        this.name = 'JourneyBotApiError';
        this.status = status;
        this.details = details;
    }
}

const requireResourceId = (value, label) => {
    if (!/^[A-Za-z0-9_-]{1,128}$/.test(value)) {
        throw new JourneyBotApiError(`${label} must contain only letters, numbers, underscores, or hyphens`, 0);
    }
    return value;
};

const request = async (path, options = {}) => {
    if (!configuredBaseUrl) {
        throw new JourneyBotApiError('REACT_APP_JOURNEYBOT_API_URL is not configured', 0);
    }

    const user = auth.currentUser;
    if (!user) {
        throw new JourneyBotApiError('You must be signed in to use JourneyBot', 401);
    }

    const token = await user.getIdToken();
    const response = await fetch(`${configuredBaseUrl.replace(/\/$/, '')}${path}`, {
        ...options,
        headers: {
            Authorization: `Bearer ${token}`,
            ...(options.body ? { 'Content-Type': 'application/json' } : {}),
            ...options.headers,
        },
    });

    const isJson = response.headers.get('content-type')?.includes('application/json');
    const body = isJson ? await response.json() : null;

    if (!response.ok) {
        if (response.status === 403 && body?.code === EMAIL_NOT_VERIFIED_CODE && typeof window !== 'undefined') {
            const returnTo = `${window.location.pathname}${window.location.search}`;
            window.location.assign(`${ROUTES.verifyEmail}?returnTo=${encodeURIComponent(returnTo)}`);
        }
        throw new JourneyBotApiError(
            body?.message ?? body?.error ?? `JourneyBot request failed with status ${response.status}`,
            response.status,
            body,
        );
    }

    return body;
};

export const submitJourneyBotRawText = (journeyId, rawText, options = {}) => {
    const id = requireResourceId(journeyId, 'Journey ID');
    const body = {
        raw_text: rawText,
        ...(options.goal ? { goal: options.goal } : {}),
        ...(options.source_provider ? { source_provider: options.source_provider } : {}),
    };

    return request(`/journeys/${id}/intake`, {
        method: 'POST',
        body: JSON.stringify(body),
    });
};

export const submitJourneyBotActionPlan = (journeyId, actionPlan) => {
    const id = requireResourceId(journeyId, 'Journey ID');
    return request(`/journeys/${id}/intake`, {
        method: 'POST',
        body: JSON.stringify({ structured: actionPlan }),
    });
};

export const updateJourneyBotActionItem = (journeyId, actionId, feedback) => {
    const id = requireResourceId(journeyId, 'Journey ID');
    const itemId = requireResourceId(actionId, 'Action ID');
    return request(`/api/v1/journeys/${id}/action-items/${itemId}`, {
        method: 'PATCH',
        body: JSON.stringify(feedback),
    });
};

export const interviewRequest = (path, body) => request(`/api/v1/interviews${path}`, body === undefined ? {} : {
    method: 'POST', body: JSON.stringify(body),
});

export const listJourneyBotJourneys = () => request('/api/v1/journeys');

export const createJourneyBotAiChatCompletion = (messages) => request('/api/v1/ai/chat', {
    method: 'POST',
    body: JSON.stringify({ messages }),
});

export const createGuestJourneyBotAiChatCompletion = (messages, { attemptId, responseKind = 'discovery' }) => {
    if (!configuredBaseUrl) {
        throw new JourneyBotApiError('REACT_APP_JOURNEYBOT_API_URL is not configured', 0);
    }

    return fetch(`${configuredBaseUrl.replace(/\/$/, '')}/api/v1/ai/guest-chat`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'X-JourneyBot-Guest-Session': getGuestSessionId(),
        },
        body: JSON.stringify({
            messages,
            guest_attempt_id: attemptId,
            response_kind: responseKind,
        }),
    }).then(async (response) => {
        const isJson = response.headers.get('content-type')?.includes('application/json');
        const body = isJson ? await response.json() : null;
        if (!response.ok) {
            throw new JourneyBotApiError(
                body?.message ?? body?.error ?? `JourneyBot request failed with status ${response.status}`,
                response.status,
                body,
            );
        }
        return body;
    });
};

export const getJourneyBotRecord = (journeyId) => {
    const id = requireResourceId(journeyId, 'Journey ID');
    return request(`/api/v1/journeys/${id}`);
};

export const getJourneyBotContext = (journeyId) => {
    const id = requireResourceId(journeyId, 'Journey ID');
    return request(`/journeys/${id}/context`);
};
